import React from "react";

import { useGetApplicationQuery, useGetPlanCostsQuery, useGetPlansQuery } from "EmployeeApplication/api";
import { MedicalPlan, PlanCost } from "Utils/types";
import { BasicLoading } from "ZeroRuby";

import { MedicalContextProvider } from "./medical.context";
import { MedicalEnrollmentModals } from "./medical.modals";
import { MedicalMultiplePlans } from "./medical.multiplePlans";
import { MedicalSinglePlan } from "./medical.singlePlan";

type Data =
  | {
      isLoading: true;
      isError: boolean;
      plans: undefined;
      planCosts: undefined;
    }
  | {
      isLoading: false;
      isError: boolean;
      plans: Array<MedicalPlan>;
      planCosts: Array<PlanCost>;
    };

const useData = (): Data => {
  const { isLoading: isLoadingApplication, data: { employeePolicy: { id } = {} } = {} } = useGetApplicationQuery();
  const {
    isLoading: isLoadingPlans,
    isError: isErrorPlans,
    data: { plans } = {},
  } = useGetPlansQuery(
    {
      id,
      type: "medical",
    },
    { skip: !id },
  );
  const {
    isLoading: isLoadingCosts,
    isError: isErrorCosts,
    data: { planCosts } = {},
  } = useGetPlanCostsQuery(
    {
      id,
      type: "medical",
    },
    { skip: isLoadingPlans || isErrorPlans || plans?.length === 0 },
  );

  return {
    isError: isErrorPlans || isErrorCosts,
    isLoading: isLoadingApplication || isLoadingPlans || isLoadingCosts,
    plans,
    planCosts,
  } as Data;
};

/**
 * This component manages what to render as term life plans and costs are loaded.
 */
const StateManager = () => {
  const { isLoading, isError, plans, planCosts } = useData();
  if (isLoading || isError || !plans || plans.length < 1 || !planCosts || planCosts.length < 1) {
    return <BasicLoading />;
  }
  if (plans.length === 1) {
    return <MedicalSinglePlan plan={plans[0]} planCost={planCosts.find(({ planID }) => planID === plans[0].id)} />;
  }
  if (plans.length > 1) {
    return <MedicalMultiplePlans plans={plans} planCosts={planCosts} />;
  }
  return null;
};

export const MedicalPage = () => (
  <MedicalContextProvider>
    <StateManager />
    <MedicalEnrollmentModals />
  </MedicalContextProvider>
);
